import { useContext, useMemo } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import PermissionMatrixContext from "@/Contexts/PermissionMatrix/Context";

const UserList = () => {
  const {
    employeeList,
    employeeListLoading,
    selectedEmploymentId,
    selectEmployment,
    selectedPermissionsLoading,
  } = useContext(PermissionMatrixContext);

  const sorted = useMemo(() => {
    // keep stable but nicer UX; you can remove sorting if backend order matters
    return [...employeeList].sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [employeeList]);

  return (
    <Box
      sx={{
        width: 320,
        borderRight: "1px solid rgba(0,0,0,0.12)",
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Users
        </Typography>
      </Box>

      {employeeListLoading ? (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading employees…
          </Typography>
        </Box>
      ) : sorted.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No employees found.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {sorted.map((emp) => {
            const selected = selectedEmploymentId === emp.id;

            return (
              <ListItemButton
                key={`${emp.id}-${emp.fullName}`}
                selected={selected}
                onClick={() => selectEmployment(emp.id)}
                sx={{
                  py: 1.75,
                  px: 2,
                  bgcolor: selected ? "rgba(25,118,210,0.55) !important" : "rgba(25,118,210,0.10)",
                  color: selected ? "#fff" : "inherit",
                  "&:hover": {
                    bgcolor: selected ? "rgba(25,118,210,0.62)" : "rgba(25,118,210,0.16)",
                  },
                  "&.Mui-selected:hover": {
                    bgcolor: "rgba(25,118,210,0.62)",
                  },
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <Avatar
                    src={emp.imageUrl ?? undefined}
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: selected ? "rgba(255,255,255,0.28)" : "rgba(0,0,0,0.25)",
                      color: "#fff",
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: selected ? 800 : 500,
                        lineHeight: 1.15,
                        color: selected ? "#fff" : "inherit",
                      }}
                    >
                      {emp.fullName}
                    </Typography>
                  }
                />

                {selected && selectedPermissionsLoading ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : null}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default UserList;
